import { NON_PHARMA_ITEMS_SCHEMA } from "$components/non-pharma-dispense/non-pharma-dispense-validation";
import { VALIDATION_SCHEMA } from "$routes/patient/_lib/add-prescription-card";
import parseInvestigationSchema from "$routes/patient/[:patientId]/_lib/investigations/parse-investigation-schema";
import { useMemo, useRef } from "react";
import * as Yup from "yup";

type Schemas = Record<string, Yup.AnyObjectSchema>;

export function useValidationSchema() {
  const schemas = useRef({} as Schemas);

  return useMemo(
    () =>
      Yup.object({
        problem: Yup.string(),
        presentingComplaint: Yup.string(),
        plan: Yup.string(),
        prescriptions: Yup.array().of(VALIDATION_SCHEMA),
        nonPharmaItems: NON_PHARMA_ITEMS_SCHEMA.required(),
        symptoms: Yup.array(
          Yup.object({
            optionId: Yup.string().required(),
            label: Yup.string().required(),
          }),
        ),
        diagnosis: Yup.array(
          Yup.object({
            key: Yup.string().required(),
            codification: Yup.object({
              name: Yup.string().required(),
              category: Yup.string().required(),
              code: Yup.string(),
            }).required(),
            disease: Yup.string(),
            freeText: Yup.boolean(),
          }),
        ).required(),
        investigations: Yup.array(
          Yup.lazy((item) => {
            // If the form has not yet been interacted with,
            // return an empty schema, so child fields are not required
            if (!item?.catalogueItem || item.isRequested) {
              return Yup.object({
                catalogueItem: Yup.object(),
                outcome: Yup.object(),
                id: Yup.string().required(),
                isRequested: Yup.boolean().nullable(),
              });
            }

            const { catalogueItem } = item;
            const { _id: investigationId } = catalogueItem;

            if (!schemas.current[investigationId]) {
              const schema = parseInvestigationSchema(catalogueItem);
              schemas.current[investigationId] = Yup.object({
                catalogueItem: Yup.object().required(),
                outcome: schema,
                id: Yup.string().required(),
                isRequested: Yup.boolean().nullable(),
              });
            }

            return schemas.current[investigationId];
          }),
        ),
      }),
    [],
  );
}

export type FormValues = Yup.Asserts<ReturnType<typeof useValidationSchema>>;
